import { AlertCircle } from 'lucide-react';
import { ImprovementSuggestionsProps } from './types';
import { ImprovementButton } from './components/improvement-button/improvement-button';
import { improvePrompt } from '../../../lib/api';
import { QuotaExceededError } from '../../../lib/custom-errors';
import { isPerfectEvaluationScore } from '../../../lib/utils';
import { useEffect, useRef, useState } from 'react';
import { ImprovementResults } from './components/improvement-results/improvement-results';
import { ImprovementLoadingResults } from './components/improvement-loading-results/improvement-loading-results';
import { useSimulations } from '../../../contexts/SimulationContext';
import type {
  EvaluationResult,
  PromptImproverSuggestion,
} from '../../../lib/types';
import ExpandableSection from '../expandable-section/expandable-section';

export const ImprovementSuggestions = ({
  workspaceId,
  simulationId,
  evaluatorId,
  agentPrompt,
  conversation,
  evaluation,
  evaluator,
  onSuggestionsRequested,
}: ImprovementSuggestionsProps) => {
  const { setSimulations } = useSimulations();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasMarkedExistingSuggestionsRef = useRef(false);

  const suggestions = evaluation?.suggestions as
    | PromptImproverSuggestion[]
    | undefined;
  const hasSuggestions = Boolean(suggestions?.length);
  const isPerfectScore = isPerfectEvaluationScore(evaluation?.score);

  useEffect(() => {
    if (hasSuggestions && !hasMarkedExistingSuggestionsRef.current) {
      hasMarkedExistingSuggestionsRef.current = true;
      onSuggestionsRequested?.();
    }
  }, [hasSuggestions, onSuggestionsRequested]);

  const handleImprovePrompt = async () => {
    if (hasSuggestions) return;
    onSuggestionsRequested?.();
    setErrorMessage(null);
    setIsLoading(true);
    if (
      !workspaceId ||
      !simulationId ||
      !evaluatorId ||
      !conversation ||
      !evaluation ||
      !evaluator
    ) {
      setErrorMessage(
        'Missing information needed to generate suggestions. Check the evaluation and workspace, then try again.',
      );
      setIsLoading(false);
      return;
    }
    try {
      const result = await improvePrompt({
        workspaceId,
        simulationId,
        evaluatorId,
        conversation,
        evaluation: {
          name: evaluator.name,
          criteria: evaluator.statement,
          result: {
            what_went_well: evaluation.success ?? '',
            could_be_improved: evaluation.failure ?? '',
            score: evaluation.score,
          },
        },
      });

      setSimulations((prev) =>
        prev.map((sim) =>
          sim.id === simulationId
            ? {
                ...sim,
                evaluations: {
                  ...(sim.evaluations ?? {}),
                  [evaluatorId]: {
                    ...(sim.evaluations?.[evaluatorId] ?? {}),
                    suggestions: result.suggestions,
                  } as EvaluationResult,
                },
              }
            : sim,
        ),
      );
    } catch (e) {
      console.error(e);
      setErrorMessage(
        e instanceof QuotaExceededError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to generate suggestions. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };
  if (isPerfectScore && !hasSuggestions && !isLoading) {
    return null;
  }

  return (
    <div data-onboarding="improvement-suggestions">
      {isLoading ? (
        <ImprovementLoadingResults />
      ) : hasSuggestions ? (
        <ExpandableSection
          title="Prompt improvement suggestions"
          defaultExpanded={true}
        >
          <ImprovementResults results={suggestions ?? []} />
        </ExpandableSection>
      ) : (
        <div className="space-y-2">
          <ImprovementButton
            handleImprovePrompt={handleImprovePrompt}
            isLoading={isLoading}
            dataOnboarding="improvement-suggestions-button"
          />
          {errorMessage ? (
            <div
              className="flex items-start gap-2 text-sm text-red-600"
              role="alert"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>{errorMessage}</span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
